import { useModal } from '@adserve/adserve-react-components';
import React, { useState } from 'react';
import { AttachmentType } from '../../../constants/Enums';
import { deleteAttachment, download, downloadAndOpen, openAttachment } from '../../../services/AttachmentService';
import { deleteNote, getNote } from '../../../services/NoteService';
import RadioDropdownButton from '../../generic/adserve-react-components/radio-dropdown-button/RadioDropdownButton';
import { notesPanelStyle } from './CampaignBriefStyles';
import CampaignAttachmentCreate from './CampaignNotes/CampaignAttachmentCreate';
import CampaignViewNote from './CampaignNotes/CampaignViewNote'; // ASSP-8023
import CampaignNotesTable from './CampaignNotes/CampaignNotesTable';
import { ReactComponent as IconPlus } from '../../../svg/Icon_Plus.svg';
import { ReactComponent as IconMinimise } from '../../../svg/Icon_Minus.svg';
import { sectionedPanelStyle, iconPlusMinimiseStyle } from '../CampaignStyles';

const whiteButtonStyle = {
    border: '2px solid #CCCCCC',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer'
};

const modalStyle = {
    width: '50%',
    fontSize: 16,
    textAlign: 'start'
};

const CampaignNotes = ({ campaign, data, changeMade, readOnly, onChange }) => {
    const { addMessage } = useModal();
    const [open, setOpen] = useState(false);

    const handleCreateClick = (id) => {
        addMessage({
            modalStyle: modalStyle,
            message: (
                <CampaignAttachmentCreate
                    attachmentTypeId={id}
                    campaignId={campaign.id}
                    onChange={onChange}
                    changeMade={changeMade}
                    notes={data}
                />
            )
        });
    };

    // ASSP-8023 getNote() copied from handleEditClick()
    const viewNote = async (dto) => {
        getNote(dto.id, 'campaign').then((note) => {
            note.data.value = JSON.parse(note.data.slateValue);
            addMessage({
                modalStyle: modalStyle,
                message: <CampaignViewNote note={note.data} />
            });
        });
};

    const handleEditClick = async (index) => {
        const dto = data[index];

        if (dto.attachmentTypeId === AttachmentType.NOTE.id) {
            if (campaign.id === window.emptyGuid || campaign.id == null) {
                addMessage({
                    modalStyle: modalStyle,
                    message: (
                        <CampaignAttachmentCreate
                            attachmentTypeId={AttachmentType.NOTE.id}
                            campaignId={campaign.id}
                            data={dto}
                            onChange={onChange}
                            changeMade={changeMade}
                            notes={data}
                            isEditing={true}
                        />
                    )
                });
            } else {
                getNote(dto.id, 'campaign').then((note) => {
                    note.data.value = JSON.parse(note.data.slateValue);
                    addMessage({
                        modalStyle: modalStyle,
                        message: (
                            <CampaignAttachmentCreate
                                attachmentTypeId={AttachmentType.NOTE.id}
                                campaignId={campaign.id}
                                data={note.data}
                                onChange={onChange}
                                changeMade={changeMade}
                                notes={data}
                                isEditing={true}
                            />
                        )
                    });
                });
            }
        } else {
            dto.file = await download(dto?.id, 'campaign');
            dto.file ? (dto.file.name = dto.fileName) : (dto.file = undefined);
            addMessage({
                modalStyle: modalStyle,
                message: (
                    <CampaignAttachmentCreate
                        attachmentTypeId={AttachmentType.ATTACHMENT.id}
                        campaignId={campaign.id}
                        data={dto}
                        onChange={onChange}
                        changeMade={changeMade}
                        notes={data}
                        isEditing={true}
                    />
                )
            });
        }
    };

    const handleDeleteClick = (index) => {
        const dto = data[index];

        const handleNote = async () => {
            if (campaign.id === window.emptyGuid || campaign.id == null) {
                changeMade(
                    'campaignNotes',
                    data.filter((d) => d.attachmentTypeId === AttachmentType.NOTE.id && d.name !== dto.name)
                );
                return;
            }

            deleteNote(dto.id, 'campaign').then(() => {
                onChange();
            });
        };

        const handleAttachment = async () => {
            if (campaign.id === window.emptyGuid || campaign.id == null) {
                changeMade(
                    'campaignAttachments',
                    data.filter((d) => d.attachmentTypeId === AttachmentType.ATTACHMENT.id && d.name !== dto.name)
                );
                return;
            }

            deleteAttachment(dto.id, 'campaign').then(() => {
                onChange();
            });
        };

        addMessage({
            message: window.translate(`Are you sure you want to delete ${dto.name}?`),
            buttons: [
                {
                    text: window.translate('No'),
                    style: { ...whiteButtonStyle, width: 87 },
                    callback: null
                },
                {
                    text: window.translate('Yes'),
                    style: { cursor: 'pointer' },
                    callback: dto.attachmentTypeId === AttachmentType.NOTE.id ? handleNote : handleAttachment
                }
            ]
        });
    };

    const handleLinkClick = async (index) => {
        const dto = data[index];
        if (dto.attachmentTypeId === AttachmentType.NOTE.id) viewNote(dto); // ASSP-8023
        else if (campaign.id === window.emptyGuid || campaign.id == null) openAttachment(dto.file);
        else await downloadAndOpen(dto.id, 'campaign');
    };

    return (
        <div style={sectionedPanelStyle}>
            <div
                style={{
                    ...notesPanelStyle,
                    paddingRight: 19,
                    paddingBottom: 8
                }}
            >
                <span style={{ fontSize: 15, fontWeight: 500, marginBottom: 17 }}>
                    {window.translate('Notes & Attachments')}
                </span>
                <span>
                    {open ? (
                        <IconMinimise
                            height={19}
                            width={19}
                            style={iconPlusMinimiseStyle}
                            onClick={() => setOpen(!open)}
                        />
                    ) : (
                        <IconPlus height={19} width={19} style={iconPlusMinimiseStyle} onClick={() => setOpen(!open)} />
                    )}
                </span>
            </div>
            {open && (
                <div
                    style={{
                        ...notesPanelStyle,
                        paddingTop: 0,
                        flexDirection: 'column'
                    }}
                >
                    <RadioDropdownButton
                        id='new-note'
                        text={window.translate('Create New')}
                        options={[AttachmentType.NOTE, AttachmentType.ATTACHMENT]}
                        containerStyle={{
                            alignSelf: 'flex-end',
                            marginBottom: 20
                        }}
                        createClick={handleCreateClick}
                        disabled={readOnly}
                    />
                    <CampaignNotesTable
                        data={data}
                        onEdit={handleEditClick}
                        onDelete={handleDeleteClick}
                        onLinkClick={handleLinkClick}
                        readOnly={readOnly}
                    />
                </div>
            )}
        </div>
    );
};

export default CampaignNotes;
